import type { TNavbarItemElement, TNavbarMenuBurgerElement, TNavbarMenuElement } from '@/navbarElement';
import type { NavbarV2SerializableNode } from '@/navbarElementV2/navbarMenuElement';
import type { InlineNode } from '@/types';
import { deepClone } from './deepClone';

const getNavbarItemTextContainer = <T>(text: string, type: string) => {
  return {
    type,
    content: [
      {
        type: 'text',
        text,
      },
    ],
  } as unknown as T;
};

export const constructMobileView = (root: TNavbarMenuElement) => {
  if (root.type !== 'navbar_menu') return;

  // Cast root to the correct type for better type inference
  const navbarMenu = deepClone(root as TNavbarMenuElement);

  // Collect all non-logo menu items from all menu lists and groups
  const burgerMenuContent: any[] = [];

  const makeBurgerMenuContent = (node: NavbarV2SerializableNode) => {
    if ('text' in node) return;
    if (node.type === 'hardBreak') return;

    if (node.type === 'navbar_item' && node.attrs?.type !== 'logo') {
      // Deep clone the item to avoid reference issues
      const clonedItem = deepClone(node);

      // Create a completely separate node with new identity
      const newItem = {
        ...clonedItem,
        attrs: {
          ...clonedItem.attrs,
          // Generate new unique ID
          id: `burger-item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          // Remove any existing customAttributes that might contain identity info
          customAttributes: {
            ...clonedItem.attrs?.customAttributes,
            // Clear any existing data attributes that might contain identity
            'data-id': undefined,
            'data-name': undefined,
          },
        },
        // Keep the content but ensure it's also properly cloned
        content: clonedItem.content ? deepClone(clonedItem.content) : undefined,
      };

      burgerMenuContent.push(newItem);
    }

    node.content?.forEach(makeBurgerMenuContent);
  };

  // Collect items from the entire navbar menu
  makeBurgerMenuContent(root as NavbarV2SerializableNode);

  // Insert burger into the first menu list group
  const leftMenuListGroup = navbarMenu.content?.[0]?.content?.[0];
  const rightMenuListGroup = navbarMenu.content?.[0]?.content?.[2];

  if (leftMenuListGroup) {
    leftMenuListGroup.content = [leftMenuListGroup?.content?.[0] as TNavbarItemElement];
  }

  const burgerElement = {
    type: 'navbar_menu_burger' as const,
    attrs: { id: `burger-${Date.now()}`, color: '#000000', size: 24 },
    content: burgerMenuContent, // Put the menu items directly in the burger content
  };

  if (rightMenuListGroup) {
    rightMenuListGroup.content = [burgerElement as unknown as TNavbarMenuBurgerElement];
  }

  // Remove middle group
  if (navbarMenu.content?.[0]?.content?.[1]) {
    navbarMenu.content?.[0]?.content.splice(1, 1);
  }

  return navbarMenu;
};

export const navbarV2Migrator = <T extends NavbarV2SerializableNode | InlineNode = NavbarV2SerializableNode>(
  navbar: T,
): NavbarV2SerializableNode => {
  const hydrateTextInElements = (node: NavbarV2SerializableNode): void => {
    if ('text' in node || node.type === 'hardBreak') return;

    node.content?.forEach(hydrateTextInElements);

    switch (node.type) {
      case 'navbar_item': {
        switch (node.attrs?.type) {
          case 'search':
          case 'logo':
          case 'user_button':
            break;

          default: {
            if (!node.attrs?.label) break;

            const labelText = node.attrs?.label || '';

            const textAlreadyExists = node.content?.some((item) => item.type === 'navbar_item_text_container');

            if (textAlreadyExists) break;

            if (node.content) {
              node.content.unshift(getNavbarItemTextContainer(labelText, 'navbar_item_text_container'));
            } else {
              node.content = [getNavbarItemTextContainer(labelText, 'navbar_item_text_container')];
            }

            break;
          }
        }
        break;
      }

      case 'navbar_menu_list': {
        // Extract line divider properties from parent
        const lineDividerProps = {
          lineDividerStyle: node.attrs?.lineDividerStyle,
          lineDividerWidth: node.attrs?.lineDividerWidth,
          lineDividerColor: node.attrs?.lineDividerColor,
          lineDividerHeight: node.attrs?.lineDividerHeight,
        };

        // Apply to all child groups
        node.content?.forEach((group) => {
          if (group.type === 'navbar_menu_list_group' && group.attrs) {
            group.attrs = {
              ...group.attrs,
              ...lineDividerProps,
            };
          }
        });

        // Migrate justify value to middle group
        const justifyValue = node.attrs?.justify;
        if (justifyValue) {
          const middleGroup = node.content?.find(
            (group) => group.type === 'navbar_menu_list_group' && group.attrs?.type === 'middle',
          ) as any;
          if (middleGroup?.attrs) {
            middleGroup.attrs.justify = justifyValue;
          }
        }

        // Remove line divider properties and justify from parent (keep X-axis ones)
        if (node.attrs) {
          const { lineDividerStyle, lineDividerWidth, lineDividerColor, lineDividerHeight, justify, ...restAttrs } =
            node.attrs;
          node.attrs = restAttrs;
        }
        break;
      }

      // case 'navbar_menu':
      // case 'navbar_dropdown':
      // case 'navbar_menu_list_group':
      // case 'navbar_dropdown_column':
      // case 'navbar_menu_burger_content':
      // case 'navbar_menu_list':
      case 'navbar_dropdown_item': {
        if (!node.content) node.content = [];

        const attrs = node.attrs;

        if (attrs?.description && !node.content.some((child) => child.type === 'navbar_dropdown_item_description')) {
          node.content.unshift(getNavbarItemTextContainer(attrs.description, 'navbar_dropdown_item_description'));
        }

        if (attrs?.title && !node.content.some((child) => child.type === 'navbar_dropdown_item_title')) {
          node.content.unshift(getNavbarItemTextContainer(attrs.title, 'navbar_dropdown_item_title'));
        }

        break;
      }
      // case 'navbar_menu_burger':
      default:
        break;
    }
  };

  const result = deepClone(navbar as NavbarV2SerializableNode);

  hydrateTextInElements(result);

  return result;
};
